import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useParams, UNSAFE_NavigationContext } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { ListChecks, Clock, Star, Layers } from 'lucide-react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { trainerService, sessionService } from '../../services';
import DraggableActivityList from '../../components/activity/DraggableActivityList';
import ActivityPreviewModal from '../../components/activity/ActivityPreviewModal';
import { notifications } from '../../utils/toast';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import {
  duplicateActivity,
  getActivityStats,
  exportActivitiesToCSV,
  exportActivitiesToJSON,
} from '../../utils/activityHelpers';
import { usePreferences } from '../../contexts/PreferencesContext';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';
import { MAX_UPLOAD_MB } from '../../utils/uploadLimits';

/**
 * Session Setup Page - Trainer
 * Create and manage sub-sessions and activities
 */

const SessionSetupPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [session, setSession] = useState(null);
  const [activities, setActivities] = useState([]);
  const [workbooks, setWorkbooks] = useState([]);
  const [preReadMaterials, setPreReadMaterials] = useState([]); // Phase 2
  const [preReadUploading, setPreReadUploading] = useState(false); // Phase 2
  const [subsessions, setSubsessions] = useState([]);
  const [sessionMode, setSessionMode] = useState('drip'); // 'non-drip' or 'drip'

  // Modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewActivity, setPreviewActivity] = useState(null);
  const [showAddSubsession, setShowAddSubsession] = useState(false);
  const [editingSubsession, setEditingSubsession] = useState(null);
  const [subsessionForm, setSubsessionForm] = useState({
    title: '',
    timeFrom: '',
    timeTo: '',
    breakAfter: 0, // break duration in minutes after this subsession
  });
  const [isCustomBreak, setIsCustomBreak] = useState(false); // Track custom break mode

  // Activity configuration modal
  const [showActivityConfig, setShowActivityConfig] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [targetSubsessionId, setTargetSubsessionId] = useState(null); // Track which subsession to add activity to
  const [editingActivity, setEditingActivity] = useState(null); // Track activity being edited
  const [activityConfig, setActivityConfig] = useState({
    title: '',
    question: '',
    questions: [{ question: '', options: ['', ''], correctAnswer: 0 }], // For quiz with multiple questions
    options: ['', ''],
    words: [''], // For word cloud predefined words
    duration: 5,
    timerEnabled: false, // Timer is optional
  });

  // Confirmation dialog states
  const [deleteActivityConfirm, setDeleteActivityConfirm] = useState(null);
  const [deleteSubsessionConfirm, setDeleteSubsessionConfirm] = useState(null);
  const [deleteWorkbookConfirm, setDeleteWorkbookConfirm] = useState(null);

  // Help popover state
  const [showSubsessionHelp, setShowSubsessionHelp] = useState(false);
  const subsessionHelpRef = useRef(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Handle back navigation with unsaved changes check
  const handleBackNavigation = (path) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  // Block navigation when there are unsaved changes using UNSAFE_NavigationContext
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);

  // Confirm leaving without saving
  const confirmLeave = () => {
    setShowBackConfirm(false);
    setHasUnsavedChanges(false);
    hasUnsavedChangesRef.current = false; // Update ref immediately to allow navigation
    if (pendingNavigation) {
      navigate(pendingNavigation);
    } else {
      navigate(-1);
    }
  };

  // Cancel leaving
  const cancelLeave = () => {
    setShowBackConfirm(false);
    setPendingNavigation(null);
  };

  // Keep ref in sync with state
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Intercept navigation
  useEffect(() => {
    if (!navigator) return;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args) => {
      if (hasUnsavedChangesRef.current) {
        const [to] = args;
        const path = typeof to === 'string' ? to : to.pathname;
        setPendingNavigation(path);
        setShowBackConfirm(true);
        return;
      }
      return originalPush.apply(navigator, args);
    };

    navigator.replace = (...args) => {
      if (hasUnsavedChangesRef.current) {
        const [to] = args;
        const path = typeof to === 'string' ? to : to.pathname;
        setPendingNavigation(path);
        setShowBackConfirm(true);
        return;
      }
      return originalReplace.apply(navigator, args);
    };

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator]);

  // Warn before closing browser tab/window with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Close subsession help popover on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (subsessionHelpRef.current && !subsessionHelpRef.current.contains(event.target)) {
        setShowSubsessionHelp(false);
      }
    };

    if (showSubsessionHelp) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSubsessionHelp]);

  useEffect(() => {
    fetchSession();
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const response = await trainerService.getSession(sessionId);
      if (response.success) {
        const foundSession = response.data;
        setSession(foundSession);

        // Collect all activities: from session.activities AND from inside subSessions
        // This handles duplicated sessions where activities are stored inside subSessions
        let allActivities = [];

        // Add activities from session.activities (with proper id)
        const sessionActivities = (foundSession.activities || []).map(activity => ({
          ...activity,
          id: activity.id || activity._id,
        }));
        allActivities.push(...sessionActivities);

        // Extract activities from subsessions and add to main activities array
        // This ensures editing works correctly for duplicated sessions and after save
        (foundSession.subSessions || []).forEach(ss => {
          // Use _id as primary (from MongoDB), fall back to id
          const subsessionId = (ss._id || ss.id)?.toString();
          (ss.activities || []).forEach(activity => {
            // Check if this activity already exists in allActivities (avoid duplicates)
            const activityId = (activity._id || activity.id)?.toString();
            const alreadyExists = allActivities.some(a => {
              const existingId = (a._id || a.id)?.toString();
              return existingId === activityId;
            });
            if (!alreadyExists && activityId) {
              allActivities.push({
                ...activity,
                id: activityId,
                _id: activity._id || activity.id,
                subsessionId: subsessionId,
              });
            }
          });
        });

        setActivities(allActivities);

        // Ensure workbooks are properly parsed objects, not strings
        let parsedWorkbooks = foundSession.workbooks || [];
        if (typeof parsedWorkbooks === 'string') {
          try {
            parsedWorkbooks = JSON.parse(parsedWorkbooks);
          } catch (e) {
            console.error('Failed to parse workbooks:', e);
            parsedWorkbooks = [];
          }
        }
        // Filter out any string elements and ensure each has required fields
        // Convert _id to string to handle MongoDB ObjectId objects
        parsedWorkbooks = parsedWorkbooks.filter(wb => typeof wb === 'object' && wb !== null).map(wb => {
          const idStr = String(wb._id || wb.id || '');
          // Extract name from URL if not present
          let workbookName = wb.name;
          if (!workbookName || workbookName === 'Untitled') {
            const url = wb.url || '';
            if (url) {
              try {
                const urlPath = new URL(url).pathname;
                const fileName = decodeURIComponent(urlPath.split('/').pop() || '');
                workbookName = fileName.replace(/^\d+-/, '') || 'Untitled';
              } catch {
                workbookName = 'Untitled';
              }
            }
          }
          return {
            ...wb,
            id: idStr,
            _id: idStr,
            name: workbookName,
          };
        });
        setWorkbooks(parsedWorkbooks);

        // Phase 2: load pre-read materials uploaded during session creation (by client) or previous sessions
        setPreReadMaterials(foundSession.preSession?.materials || []);

        // Normalize subsessions - use consistent ID format and clear activities
        // Activities are now managed in the main activities array
        const normalizedSubsessions = (foundSession.subSessions || []).map(ss => {
          const ssId = (ss._id || ss.id)?.toString();
          return {
            ...ss,
            id: ssId,
            _id: ss._id || ss.id,
            activities: [], // Activities are now in main activities array
          };
        });
        setSubsessions(normalizedSubsessions);

        if (foundSession.sessionMode) {
          setSessionMode(foundSession.sessionMode);
        }
      }
    } catch (error) {
      console.error('Error fetching session:', error);
    }
  };

  // Save all changes handler
  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Ensure workbooks are clean objects with required fields
      const cleanWorkbooks = workbooks.map(wb => ({
        name: wb.name,
        url: wb.url,
        type: wb.type || 'unknown',
        size: wb.size || 0,
        uploadedAt: wb.uploadedAt || new Date().toISOString(),
        ...(wb._id ? { _id: wb._id } : {}),
        ...(wb.id && !wb._id ? { id: wb.id } : {}),
      }));

      const response = await trainerService.updateSessionSetup(sessionId, {
        subSessions: subsessions,
        activities: activities,
        workbooks: cleanWorkbooks,
        sessionMode: sessionMode,
      });

      if (response.success) {
        notifications.success('All changes saved successfully');
        setHasUnsavedChanges(false); // Reset dirty flag after successful save
        // Update local state with saved data
        if (response.data) {
          setSession(response.data);

          // Process subsessions and extract activities
          const savedSubsessions = response.data.subSessions || [];
          // Clear embedded activities - they are now managed in the main activities array
          // This ensures the render logic uses the main activities array, not embedded ones
          setSubsessions(savedSubsessions.map(ss => ({
            ...ss,
            id: (ss._id || ss.id)?.toString(),
            _id: ss._id || ss.id,
            activities: [], // Clear embedded activities so render uses main array
          })));

          // Extract ALL activities: both standalone and those inside subsessions
          // 1. Standalone activities (not assigned to any subsession)
          const standaloneActivities = (response.data.activities || []).map((act, index) => {
            const actId = (act._id || act.id || `activity-${Date.now()}-${index}`).toString();
            return {
              ...act,
              id: actId,
              _id: actId,
              subsessionId: null,
            };
          });

          // 2. Activities inside subsessions
          const subsessionActivities = savedSubsessions.flatMap(ss => {
            const ssId = (ss._id || ss.id).toString();
            return (ss.activities || []).map((act, index) => {
              const actId = (act._id || act.id || `activity-${Date.now()}-${ssId}-${index}`).toString();
              return {
                ...act,
                id: actId,
                _id: actId,
                subsessionId: ssId,
              };
            });
          });

          // Combine all activities
          const allActivities = [...standaloneActivities, ...subsessionActivities];
          console.log('[SessionSetup] Restored activities after save:', allActivities.length, allActivities.map(a => ({ id: a.id, title: a.title, subsessionId: a.subsessionId })));
          setActivities(allActivities);

          // Normalize workbooks to ensure id field and name are set
          const normalizedWorkbooks = (response.data.workbooks || []).map(wb => {
            const idStr = String(wb._id || wb.id || '');
            // Extract name from URL if not present
            let workbookName = wb.name;
            if (!workbookName || workbookName === 'Untitled') {
              const url = wb.url || '';
              if (url) {
                try {
                  // Extract filename from URL (remove path and query params)
                  const urlPath = new URL(url).pathname;
                  const fileName = decodeURIComponent(urlPath.split('/').pop() || '');
                  // Remove timestamp prefix if present (e.g., "1234567890-filename.pdf" -> "filename.pdf")
                  workbookName = fileName.replace(/^\d+-/, '') || 'Untitled';
                } catch {
                  workbookName = 'Untitled';
                }
              }
            }
            return {
              ...wb,
              id: idStr,
              _id: idStr,
              name: workbookName,
            };
          });
          setWorkbooks(normalizedWorkbooks);
        }
      } else {
        notifications.error('Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving session setup:', error);
      notifications.error('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Start session handler
  const handleStartSession = async () => {
    setIsStarting(true);
    try {
      // Save any pending changes first
      await handleSaveAllChanges();

      // Then launch the session
      const response = await trainerService.launchSession(sessionId);
      if (response.success) {
        notifications.success('Session started successfully!');
        // Navigate to session control page
        navigate(`/trainer/session/${sessionId}/control`);
      } else {
        notifications.error(response.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      // Show specific error message from API
      notifications.error(error.message || 'Failed to start session. Please try again.');
    } finally {
      setIsStarting(false);
    }
  };

  // Keyboard shortcuts
  const anyModalOpen = showPreviewModal || showAddSubsession;

  useKeyboardShortcut(['ctrl+s', 'cmd+s'], () => {
    if (!anyModalOpen && !isSaving) {
      handleSaveAllChanges();
    }
  });

  useKeyboardShortcut(['ctrl+e', 'cmd+e'], () => {
    if (!anyModalOpen && activities.length > 0) {
      handleExportActivities('csv');
    }
  });

  useKeyboardShortcut(['escape', 'esc'], () => {
    if (showPreviewModal) setShowPreviewModal(false);
    else if (showAddSubsession) setShowAddSubsession(false);
    else if (showActivityConfig) setShowActivityConfig(false);
  });

  // Simple activity types with SVG icons - using consistent colors from activityIcons utility
  const activityTypes = [
    {
      type: 'quiz',
      name: 'Quiz',
      icon: getActivityIcon('quiz', 'lg'),
      description: 'Multiple choice questions with correct answers',
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-100',
      textColor: 'text-amber-600',
    },
    {
      type: 'poll',
      name: 'Poll',
      icon: getActivityIcon('poll', 'lg'),
      description: 'Quick opinion polls and voting',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
    },
    {
      type: 'wordcloud',
      name: 'Word Cloud',
      icon: getActivityIcon('wordcloud', 'lg'),
      description: 'Collect words and display as word cloud',
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-600',
    },
    {
      type: 'openquestion',
      name: 'Open Question',
      icon: getActivityIcon('openquestion', 'lg'),
      description: 'Free-text responses and discussions',
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-100',
      textColor: 'text-emerald-600',
    },
    {
      type: 'challenges',
      name: 'Challenges',
      icon: getActivityIcon('challenges', 'lg'),
      description: 'Collect challenges participants are facing',
      color: 'from-rose-500 to-rose-600',
      bgColor: 'bg-rose-100',
      textColor: 'text-rose-600',
    },
    {
      type: 'actionplans',
      name: 'Action Plans',
      icon: getActivityIcon('actionplans', 'lg'),
      description: 'Collect action plans from participants',
      color: 'from-sky-500 to-sky-600',
      bgColor: 'bg-sky-100',
      textColor: 'text-sky-600',
    },
    {
      type: 'feedback',
      name: 'Feedback',
      icon: getActivityIcon('feedback', 'lg'),
      description: 'Collect session feedback and ratings',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-500',
    },
  ];

  // Activity management handlers
  const handleOpenActivityConfig = (activityType, subsessionId = null) => {
    setSelectedActivityType(activityType);
    setTargetSubsessionId(subsessionId);
    setActivityConfig({
      title: '',
      question: '',
      questions: activityType.type === 'quiz' ? [{ question: '', options: ['', ''], correctAnswer: 0 }] : [],
      options: activityType.type === 'poll' ? ['', ''] : [],
      words: activityType.type === 'wordcloud' ? [''] : [],
      duration: 5,
      timerEnabled: false,
    });
    setShowActivityConfig(true);
  };

  const handleActivityConfigChange = (field, value) => {
    setActivityConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddOption = () => {
    setActivityConfig((prev) => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const handleRemoveOption = (index) => {
    setActivityConfig((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleOptionChange = (index, value) => {
    setActivityConfig((prev) => ({
      ...prev,
      options: prev.options.map((opt, i) => (i === index ? value : opt)),
    }));
  };

  // Word cloud word handlers
  const handleAddWord = () => {
    setActivityConfig((prev) => ({
      ...prev,
      words: [...prev.words, ''],
    }));
  };

  const handleRemoveWord = (index) => {
    setActivityConfig((prev) => ({
      ...prev,
      words: prev.words.filter((_, i) => i !== index),
    }));
  };

  const handleWordChange = (index, value) => {
    setActivityConfig((prev) => ({
      ...prev,
      words: prev.words.map((word, i) => (i === index ? value : word)),
    }));
  };

  // Quiz multiple questions handlers
  const handleAddQuestion = () => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: [...prev.questions, { question: '', options: ['', ''], correctAnswer: 0 }],
    }));
  };

  const handleRemoveQuestion = (questionIndex) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== questionIndex),
    }));
  };

  const handleQuestionChange = (questionIndex, value) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, question: value } : q
      ),
    }));
  };

  const handleQuestionOptionChange = (questionIndex, optionIndex, value) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex
          ? { ...q, options: q.options.map((opt, j) => (j === optionIndex ? value : opt)) }
          : q
      ),
    }));
  };

  const handleAddQuestionOption = (questionIndex) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, options: [...q.options, ''] } : q
      ),
    }));
  };

  const handleRemoveQuestionOption = (questionIndex, optionIndex) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex
          ? { ...q, options: q.options.filter((_, j) => j !== optionIndex) }
          : q
      ),
    }));
  };

  const handleSetCorrectAnswer = (questionIndex, optionIndex) => {
    setActivityConfig((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === questionIndex ? { ...q, correctAnswer: optionIndex } : q
      ),
    }));
  };

  const handleSaveActivity = () => {
    if (!activityConfig.title.trim()) {
      notifications.error('Please enter an activity title');
      return;
    }

    const isEditing = !!editingActivity;

    // Validation for quiz type with multiple questions
    if (selectedActivityType.type === 'quiz') {
      if (activityConfig.questions.length === 0) {
        notifications.error('Please add at least one question');
        return;
      }
      for (let i = 0; i < activityConfig.questions.length; i++) {
        const q = activityConfig.questions[i];
        if (!q.question.trim()) {
          notifications.error(`Please enter question text for question ${i + 1}`);
          return;
        }
        const validOptions = q.options.filter(opt => opt.trim());
        if (validOptions.length < 2) {
          notifications.error(`Question ${i + 1} needs at least 2 options`);
          return;
        }
      }

      const activityData = {
        id: isEditing ? editingActivity.id : `activity-${Date.now()}`,
        _id: isEditing ? editingActivity._id : undefined, // Preserve MongoDB _id if editing
        type: selectedActivityType.type,
        title: activityConfig.title,
        description: `${activityConfig.questions.length} question${activityConfig.questions.length > 1 ? 's' : ''}`,
        duration: activityConfig.timerEnabled ? activityConfig.duration : null,
        timeLimit: activityConfig.timerEnabled ? activityConfig.duration * 60 : null,
        timerEnabled: activityConfig.timerEnabled,
        required: true, // All activities are required
        subsessionId: targetSubsessionId,
        config: {
          questions: activityConfig.questions.map(q => ({
            question: q.question,
            options: q.options.filter(opt => opt.trim()),
            correctAnswer: q.correctAnswer,
          })),
        },
      };

      if (isEditing) {
        // Update existing activity
        // Only compare _id if it's defined to avoid undefined === undefined matching all activities
        setActivities(activities.map(a =>
          (a.id === editingActivity.id || (editingActivity._id && a._id === editingActivity._id)) ? activityData : a
        ));
        notifications.activityUpdated();
      } else {
        // Add new activity
        setActivities([...activities, activityData]);
        notifications.activityAdded();
      }

      setHasUnsavedChanges(true);
      setShowActivityConfig(false);
      setTargetSubsessionId(null);
      setEditingActivity(null);
      return;
    }

    // Validation for other activity types (poll, open question, etc.)
    // Skip question validation for feedback type (uses predefined 5 questions)
    if (selectedActivityType.type !== 'feedback' && !activityConfig.question.trim()) {
      notifications.error('Please enter a question');
      return;
    }
    if (selectedActivityType.type === 'poll' &&
        activityConfig.options.filter(opt => opt.trim()).length < 2) {
      notifications.error('Please add at least 2 options');
      return;
    }

    // For feedback activities, use "5 Questions" as description since questions are predefined
    const activityDescription = selectedActivityType.type === 'feedback'
      ? '5 Questions'
      : activityConfig.question;

    const activityData = {
      id: isEditing ? editingActivity.id : `activity-${Date.now()}`,
      _id: isEditing ? editingActivity._id : undefined, // Preserve MongoDB _id if editing
      type: selectedActivityType.type,
      title: activityConfig.title,
      description: activityDescription,
      duration: activityConfig.timerEnabled ? activityConfig.duration : null,
      timeLimit: activityConfig.timerEnabled ? activityConfig.duration * 60 : null,
      timerEnabled: activityConfig.timerEnabled,
      required: true, // All activities are required
      subsessionId: targetSubsessionId,
      config: {
        question: activityConfig.question,
        options: activityConfig.options.filter(opt => opt.trim()),
        // Include words for word cloud activities
        ...(selectedActivityType.type === 'wordcloud' && {
          words: activityConfig.words.filter(word => word.trim()),
        }),
      },
    };

    if (isEditing) {
      // Update existing activity
      // Only compare _id if it's defined to avoid undefined === undefined matching all activities
      setActivities(activities.map(a =>
        (a.id === editingActivity.id || (editingActivity._id && a._id === editingActivity._id)) ? activityData : a
      ));
      notifications.activityUpdated();
    } else {
      // Add new activity
      setActivities([...activities, activityData]);
      notifications.activityAdded();
    }

    setHasUnsavedChanges(true);
    setShowActivityConfig(false);
    setTargetSubsessionId(null);
    setEditingActivity(null);
  };

  const handlePreviewActivity = (activity) => {
    setPreviewActivity(activity);
    setShowPreviewModal(true);
  };

  const handleConfirmAddActivity = (activity) => {
    setActivities([...activities, activity]);
    setShowPreviewModal(false);
    setPreviewActivity(null);
    setHasUnsavedChanges(true);
    notifications.activityAdded();
  };

  const handleEditActivity = (activity) => {
    // Block edits on live activities — matches backend guard at updateActivity.
    // Trainer must end the activity before editing its content.
    if (activity.status === 'active') {
      notifications.error('Cannot edit a live activity. Please end it first.');
      return;
    }

    // Find the activity type from activityTypes array
    const actType = activityTypes.find(t => t.type === activity.type) ||
                    activityTypes.find(t => t.type === activity.type?.toLowerCase()?.replace('_', '')) ||
                    activityTypes.find(t => t.type === 'openquestion'); // fallback

    if (!actType) {
      notifications.error('Unable to edit this activity type');
      return;
    }

    // Populate the activity config with existing data
    const config = activity.config || {};

    // Handle quiz type with multiple questions
    if (activity.type === 'quiz') {
      setActivityConfig({
        title: activity.title || '',
        question: '',
        questions: config.questions?.length > 0
          ? config.questions.map(q => ({
              question: q.question || '',
              options: q.options?.length > 0 ? q.options : ['', ''],
              correctAnswer: q.correctAnswer || 0,
            }))
          : [{ question: config.question || '', options: config.options || ['', ''], correctAnswer: config.correctAnswer || 0 }],
        options: [],
        words: [],
        duration: activity.duration || 5,
        timerEnabled: activity.timerEnabled || false,
      });
    } else {
      // Handle other activity types (poll, wordcloud, openquestion, challenges, actionplans, feedback)
      setActivityConfig({
        title: activity.title || '',
        question: config.question || activity.description || '',
        questions: [],
        options: config.options?.length > 0 ? config.options : ['', ''],
        words: config.words?.length > 0 ? config.words : [''],
        duration: activity.duration || 5,
        timerEnabled: activity.timerEnabled || false,
      });
    }

    setSelectedActivityType(actType);
    setTargetSubsessionId(activity.subsessionId || null);
    setEditingActivity(activity);
    setShowPreviewModal(false);
    setShowActivityConfig(true);
  };

  const handleDeleteActivity = (activity) => {
    setDeleteActivityConfirm(activity);
  };

  const confirmDeleteActivity = () => {
    if (deleteActivityConfirm) {
      const deleteId = deleteActivityConfirm.id || deleteActivityConfirm._id;
      setActivities(activities.filter((a) => {
        const activityId = a.id || a._id;
        return activityId !== deleteId;
      }));
      setHasUnsavedChanges(true);
      notifications.activityDeleted();
      setDeleteActivityConfirm(null);
    }
  };

  const handleDuplicateActivity = (activity) => {
    const duplicated = duplicateActivity(activity, activities);
    if (duplicated) {
      setActivities([...activities, duplicated]);
      setHasUnsavedChanges(true);
      notifications.activityDuplicated();
    }
  };

  const handleReorderActivities = (reorderedActivities, subsessionId = null) => {
    if (subsessionId) {
      // Reordering activities within a specific subsession
      // Keep unassigned and other subsession activities, replace this subsession's activities
      const otherActivities = activities.filter(a => a.subsessionId !== subsessionId);
      setActivities([...otherActivities, ...reorderedActivities]);
    } else {
      // Reordering unassigned activities
      // Keep assigned activities, replace unassigned activities
      const assignedActivities = activities.filter(a => a.subsessionId);
      setActivities([...reorderedActivities, ...assignedActivities]);
    }
    setHasUnsavedChanges(true);
  };

  const handleAssignToSubsession = (activity, targetSubsessionId) => {
    // Move activity to a different subsession (or unassign if targetSubsessionId is null)
    setActivities(activities.map(a =>
      (a.id === activity.id || a._id === activity._id)
        ? { ...a, subsessionId: targetSubsessionId }
        : a
    ));
    setHasUnsavedChanges(true);
  };

  // Subsession management handlers
  const handleOpenAddSubsession = () => {
    setSubsessionForm({
      title: '',
      timeFrom: '',
      timeTo: '',
      breakAfter: 0,
    });
    setIsCustomBreak(false);
    setEditingSubsession(null);
    setShowAddSubsession(true);
  };

  const handleOpenEditSubsession = (subsession) => {
    const breakValue = subsession.breakAfter || 0;
    const isCustomValue = breakValue > 0 && ![5, 10, 15, 20, 30, 45, 60].includes(Number(breakValue));
    setSubsessionForm({
      title: subsession.title || subsession.name,
      timeFrom: subsession.timeFrom,
      timeTo: subsession.timeTo,
      breakAfter: breakValue,
    });
    setIsCustomBreak(isCustomValue);
    setEditingSubsession(subsession);
    setShowAddSubsession(true);
  };

  const handleSubsessionFormChange = (e) => {
    const { name, value } = e.target;
    // Convert breakAfter to number since it's a numeric field
    const processedValue = name === 'breakAfter' ? Number(value) || 0 : value;
    setSubsessionForm((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handleSaveSubsession = () => {
    if (!subsessionForm.title.trim()) {
      notifications.error('Please enter a subsession title');
      return;
    }

    if (editingSubsession) {
      // Update existing subsession
      setSubsessions(subsessions.map((s) =>
        s.id === editingSubsession.id
          ? { ...s, ...subsessionForm }
          : s
      ));
      notifications.sessionUpdated();
    } else {
      // Add new subsession
      const newSubsession = {
        id: `subsession-${Date.now()}`,
        ...subsessionForm,
        order: subsessions.length,
      };
      setSubsessions([...subsessions, newSubsession]);

      // Create default activities for the new subsession
      // Timer is optional - default activities have timer disabled
      // Default order: Challenges → Action Plans → Feedback
      const defaultActivities = [
        {
          id: `activity-${Date.now()}-challenges`,
          type: 'challenges',
          title: 'Share Your Challenges',
          question: 'What challenges are you facing in your current role?',
          subsessionId: newSubsession.id,
          order: activities.length,
          duration: null,
          timeLimit: null,
          timerEnabled: false,
          required: true,
        },
        {
          id: `activity-${Date.now() + 1}-actionplans`,
          type: 'actionplans',
          title: 'Create Your Action Plan',
          question: 'What specific steps will you take to improve and achieve your goals?',
          subsessionId: newSubsession.id,
          order: activities.length + 1,
          duration: null,
          timeLimit: null,
          timerEnabled: false,
          required: true,
        },
        {
          id: `activity-${Date.now() + 2}-feedback`,
          type: 'feedback',
          title: 'Session Feedback',
          description: '5 Questions',
          subsessionId: newSubsession.id,
          order: activities.length + 2,
          duration: null,
          timeLimit: null,
          timerEnabled: false,
          required: true,
        },
      ];

      setActivities([...activities, ...defaultActivities]);
      notifications.sessionUpdated();
    }

    setHasUnsavedChanges(true);
    setShowAddSubsession(false);
    setEditingSubsession(null);
  };

  const handleDeleteSubsession = (subsessionId) => {
    setDeleteSubsessionConfirm(subsessionId);
  };

  const confirmDeleteSubsession = () => {
    if (deleteSubsessionConfirm) {
      // Remove subsession and delete all activities under it
      setSubsessions(subsessions.filter((s) => s.id !== deleteSubsessionConfirm));
      setActivities(activities.filter((a) => a.subsessionId !== deleteSubsessionConfirm));
      setHasUnsavedChanges(true);
      notifications.sessionUpdated();
      setDeleteSubsessionConfirm(null);
    }
  };

  const handleAssignActivityToSubsession = (activityId, subsessionId) => {
    setActivities(activities.map((a) =>
      a.id === activityId ? { ...a, subsessionId } : a
    ));
    setHasUnsavedChanges(true);
    notifications.success('Activity assigned to subsession');
  };

  const handleExportActivities = (format = 'csv') => {
    try {
      if (format === 'csv') {
        exportActivitiesToCSV(activities);
        notifications.activitiesExported('csv');
      } else if (format === 'json') {
        exportActivitiesToJSON(activities, {
          name: session?.name,
          description: session?.description,
        });
        notifications.activitiesExported('json');
      }
    } catch (error) {
      notifications.error(`Export failed: ${error.message}`);
    }
  };

  // Workbook management handlers
  const handleUploadWorkbook = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    notifications.info(`Uploading ${fileArray.length} ${fileArray.length === 1 ? 'file' : 'files'}...`);

    try {
      // Upload files to server one by one, keeping track of original file names
      const uploadPromises = fileArray.map(async (file) => {
        try {
          const result = await trainerService.uploadWorkbook(sessionId, file);
          // Return both server data and original file name as fallback
          return {
            ...result.data,
            // Ensure name is set - use server response name, fallback to original file name
            name: result.data?.name || file.name,
          };
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          throw new Error(`Failed to upload ${file.name}: ${err.message}`);
        }
      });

      const uploadedWorkbooks = await Promise.all(uploadPromises);

      // Debug: log what server returned
      console.log('[handleUploadWorkbook] Server returned workbooks:', uploadedWorkbooks);

      // Normalize uploaded workbooks to ensure id field is set (consistent with existing workbooks)
      const normalizedUploadedWorkbooks = uploadedWorkbooks.map(wb => {
        const idStr = String(wb._id || wb.id || '');
        console.log('[handleUploadWorkbook] Normalizing workbook:', { original_id: wb._id, name: wb.name, normalized: idStr });
        return {
          ...wb,
          id: idStr,
          _id: idStr,
        };
      });

      console.log('[handleUploadWorkbook] Normalized workbooks:', normalizedUploadedWorkbooks);

      // Update local state with uploaded workbooks
      setWorkbooks([...workbooks, ...normalizedUploadedWorkbooks]);
      notifications.success(`${uploadedWorkbooks.length} ${uploadedWorkbooks.length === 1 ? 'file' : 'files'} uploaded successfully`);
    } catch (error) {
      notifications.error(error.message || 'Failed to upload files');
    }

    // Clear input
    event.target.value = '';
  };

  const handleDeleteWorkbook = (workbookId) => {
    setDeleteWorkbookConfirm(workbookId);
  };

  const confirmDeleteWorkbook = async () => {
    if (deleteWorkbookConfirm) {
      try {
        const confirmId = String(deleteWorkbookConfirm);

        // Debug logging
        console.log('[deleteWorkbook] Confirm ID:', confirmId);
        console.log('[deleteWorkbook] All workbooks:', workbooks.map(w => ({ id: w.id, _id: w._id, name: w.name })));

        // Find the workbook to get its server ID (use string comparison)
        const workbookToDelete = workbooks.find(
          (w) => String(w.id) === confirmId || String(w._id) === confirmId
        );
        console.log('[deleteWorkbook] Found workbook:', workbookToDelete);

        const serverId = workbookToDelete?._id || deleteWorkbookConfirm;
        console.log('[deleteWorkbook] Server ID to delete:', serverId);

        // Delete from server if it has a server ID (not a temporary local ID)
        if (serverId && !String(serverId).startsWith('workbook-')) {
          await trainerService.deleteWorkbook(sessionId, String(serverId));
        }

        // Remove from local state (use string comparison)
        setWorkbooks(workbooks.filter((w) => String(w.id) !== confirmId && String(w._id) !== confirmId));
        notifications.success('Workbook deleted');
      } catch (error) {
        notifications.error(`Failed to delete workbook: ${error.message}`);
      }
      setDeleteWorkbookConfirm(null);
    }
  };

  // ==================== PHASE 2: PRE-READ MATERIALS ====================

  const handleUploadPreRead = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    setPreReadUploading(true);
    notifications.info(`Uploading ${fileArray.length} ${fileArray.length === 1 ? 'file' : 'files'}...`);

    try {
      const uploaded = [];
      for (const file of fileArray) {
        try {
          const result = await trainerService.uploadPreRead(sessionId, file);
          if (result?.success && result.data) {
            uploaded.push(result.data);
          } else {
            notifications.error(`Failed to upload ${file.name}`);
          }
        } catch (err) {
          notifications.error(`Failed to upload ${file.name}: ${err.message}`);
        }
      }

      if (uploaded.length > 0) {
        const nextMaterials = [...preReadMaterials, ...uploaded];
        setPreReadMaterials(nextMaterials);

        // Persist to session.preSession.materials
        await sessionService.updateSession(sessionId, {
          preSession: {
            ...(session?.preSession || {}),
            enabled: true,
            materials: nextMaterials,
          },
        });
        notifications.success(`${uploaded.length} ${uploaded.length === 1 ? 'file' : 'files'} uploaded successfully`);
      }
    } catch (error) {
      notifications.error(error.message || 'Failed to upload pre-read materials');
    } finally {
      setPreReadUploading(false);
      event.target.value = '';
    }
  };

  const handleDeletePreRead = async (idx) => {
    const nextMaterials = preReadMaterials.filter((_, i) => i !== idx);
    setPreReadMaterials(nextMaterials);
    try {
      await sessionService.updateSession(sessionId, {
        preSession: {
          ...(session?.preSession || {}),
          enabled: session?.preSession?.enabled !== false,
          materials: nextMaterials,
        },
      });
      notifications.success('Pre-read material removed');
    } catch (err) {
      notifications.error(`Failed to remove material: ${err.message}`);
      // Roll back local state on failure
      setPreReadMaterials(preReadMaterials);
    }
  };


  if (!session) {
    return (
      <DashboardLayout title="Session Setup">
        <div className="p-6">
          <p className="text-neutral-600">Session not found</p>
        </div>
      </DashboardLayout>
    );
  }

  // Calculate activity stats - include activities from both top-level and subsessions
  const allActivities = [
    ...activities,
    ...subsessions.flatMap(ss => ss.activities || [])
  ];
  const activityStats = getActivityStats(allActivities);

  // Total Duration = scheduled session window (endTime - startTime) in minutes
  const scheduledDurationMin = (() => {
    if (session?.startTime && session?.endTime) {
      const diff = new Date(session.endTime) - new Date(session.startTime);
      if (!isNaN(diff) && diff > 0) return Math.round(diff / 60000);
    }
    if (session?.duration) return session.duration;
    return 0;
  })();

  return (
    <DashboardLayout title="Session Setup">
      <div className="p-4 space-y-4 overflow-hidden">
        {/* Back Button */}
        <button
          onClick={() => handleBackNavigation('/trainer/sessions')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back</span>
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 break-words">{session.name}</h1>
            <p className="text-sm sm:text-base text-neutral-600 mt-1">
              {(() => {
                if (!session.startTime) {
                  return session.scheduledDate ? formatDateWithTimezone(session.scheduledDate) : 'Not scheduled';
                }
                const start = new Date(session.startTime);
                const end = session.endTime ? new Date(session.endTime) : null;
                const isMultiDay = end && start.toISOString().slice(0, 10) !== end.toISOString().slice(0, 10);

                if (isMultiDay) {
                  const startDateStr = formatDateWithTimezone(session.startTime, { day: 'numeric', month: 'short' });
                  const endDateStr = formatDateWithTimezone(session.endTime, { day: 'numeric', month: 'short', year: 'numeric' });
                  const startTimeStr = formatTime(session.startTime);
                  const endTimeStr = formatTime(session.endTime);
                  return `${startDateStr} - ${endDateStr} • ${startTimeStr} - ${endTimeStr} (daily)`;
                } else {
                  const dateStr = formatDateWithTimezone(session.startTime, { day: 'numeric', month: 'short', year: 'numeric' });
                  const startTimeStr = formatTime(session.startTime);
                  const endTimeStr = end ? formatTime(session.endTime) : '--:--';
                  return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
                }
              })()}
            </p>
          </div>
          {session.status === 'scheduled' && (
            <Button
              variant="primary"
              onClick={handleStartSession}
              disabled={isStarting}
              className="w-full sm:w-auto flex-shrink-0"
            >
              {isStarting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Starting...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Start Session
                </>
              )}
            </Button>
          )}
        </div>

        {/* Session Stats Banner */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Activities" value={activityStats.total}            icon={ListChecks} variant="blue" />
          <StatCard label="Total Duration"   value={`${scheduledDurationMin} min`}    icon={Clock}      variant="emerald" />
          <StatCard label="Required"         value={activityStats.requiredCount}     icon={Star}       variant="amber" />
          <StatCard label="Subsessions"      value={subsessions.length}              icon={Layers}     variant="violet" />
        </div>

        {/* Session Workbooks */}
        <Card>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Session Workbooks</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Upload and manage training materials for this session
                </p>
                {/* Stated up front because the limit is enforced by nginx, which rejects
                    the request before the API can return a useful message — an oversized
                    file otherwise surfaces as a generic server error. */}
                <p className="text-xs text-neutral-500 mt-1">
                  PDF or PowerPoint · max {MAX_UPLOAD_MB} MB per file
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <Badge variant="default">
                  {workbooks.length} {workbooks.length === 1 ? 'file' : 'files'}
                </Badge>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => document.getElementById('workbook-upload').click()}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Files
                </Button>
                <input
                  id="workbook-upload"
                  type="file"
                  multiple
                  accept=".pdf,.ppt,.pptx"
                  onChange={handleUploadWorkbook}
                  className="hidden"
                />
              </div>
            </div>

            {/* File Type Notice */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-amber-800">
                <span className="font-medium">Accepted formats:</span> PDF, PPT, PPTX only <span className="text-amber-700">· Max file size: 50 MB</span>
              </p>
            </div>

            {workbooks.length > 0 ? (
              <div className="space-y-2">
                {workbooks.map((file, index) => (
                  <div
                    key={file.id || file._id || file.url || `workbook-${index}`}
                    className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <svg className="w-8 h-8 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="min-w-0">
                        <p className="font-medium text-neutral-900 truncate">{file.name || 'Untitled'}</p>
                        <p className="text-xs text-neutral-500">
                          {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Size unknown'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          // Open file in new tab
                          window.open(file.url, '_blank');
                        }}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </Button>
                      <button
                        onClick={() => handleDeleteWorkbook(file._id || file.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete workbook"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-neutral-200 rounded-lg">
                <svg className="w-12 h-12 text-neutral-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm text-neutral-600">No workbooks uploaded yet</p>
                <p className="text-xs text-neutral-500 mt-1">Upload training materials and resources for this session</p>
              </div>
            )}
          </div>
        </Card>


        {/* Phase 2: Pre-Read Materials — only shown when pre-session is enabled */}
        {session?.preSession?.enabled && (
          <Card>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Pre-Read Materials</h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Upload materials participants should read before the training session
                  </p>
                  {/* Stated up front because the limit is enforced by nginx, which
                      rejects the request before the API can return a useful message —
                      an oversized file otherwise surfaces as a generic server error. */}
                  <p className="text-xs text-neutral-500 mt-1">
                    PDF, Word, PowerPoint, images or video · max {MAX_UPLOAD_MB} MB per file
                  </p>
                </div>
                <label className={`inline-flex items-center px-4 py-2 rounded-lg cursor-pointer font-medium text-sm whitespace-nowrap ${preReadUploading ? 'bg-neutral-200 text-neutral-500 cursor-wait' : 'bg-primary-600 text-white hover:bg-primary-700'}`}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {preReadUploading ? 'Uploading...' : 'Upload Material'}
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.ppt,.pptx,.doc,.docx,image/*,video/*"
                    onChange={handleUploadPreRead}
                    disabled={preReadUploading}
                    className="hidden"
                  />
                </label>
              </div>

              {preReadMaterials.length === 0 ? (
                <div className="py-12 text-center">
                  <svg className="w-12 h-12 mx-auto text-neutral-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-sm text-neutral-600">No pre-read materials uploaded yet</p>
                  <p className="text-xs text-neutral-500 mt-1">Upload PDFs, docs, or presentations for participants to review before training</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {preReadMaterials.map((m, idx) => (
                    <li key={m._id || m.url || idx} className="flex items-center justify-between gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <svg className="w-5 h-5 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-neutral-900 truncate">{m.name || 'Untitled'}</p>
                          <p className="text-xs text-neutral-500">
                            {m.type || 'file'}
                            {m.size ? ` · ${Math.round(m.size / 1024)} KB` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {m.url && (
                          <a
                            href={m.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary-600 hover:text-primary-700 px-2 py-1"
                          >
                            View
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeletePreRead(idx)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                          aria-label="Remove material"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        )}


        {/* Subsessions Management */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Subsessions</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Organize activities into subsections for better structure
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={handleOpenAddSubsession}>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Subsession
                </Button>

                {/* Help Icon with Popover */}
                <div className="relative" ref={subsessionHelpRef}>
                  <button
                    type="button"
                    onClick={() => setShowSubsessionHelp(!showSubsessionHelp)}
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-600 hover:text-neutral-800 transition-colors"
                    aria-label="What is a subsession?"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>

                  {/* Subsession Help Popover */}
                  {showSubsessionHelp && (
                    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-neutral-200 z-50 overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-primary-50 to-blue-50 border-b border-neutral-100">
                        <h4 className="font-semibold text-neutral-900 flex items-center gap-2">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Understanding Subsessions
                        </h4>
                        <button
                          type="button"
                          onClick={() => setShowSubsessionHelp(false)}
                          className="text-neutral-500 hover:text-neutral-700 p-1 rounded-full hover:bg-neutral-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Content */}
                      <div className="p-4 space-y-4 text-sm">
                        {/* What is a Subsession */}
                        <div>
                          <h5 className="font-semibold text-neutral-800 mb-1 flex items-center gap-2">
                            <span className="w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                            What is a Subsession?
                          </h5>
                          <p className="text-neutral-600 pl-7">
                            A subsession is a section or module within your training session. It helps you organize activities into logical groups like "Introduction", "Main Content", or "Q&A".
                          </p>
                        </div>

                        {/* What happens after adding */}
                        <div>
                          <h5 className="font-semibold text-neutral-800 mb-1 flex items-center gap-2">
                            <span className="w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                            After Adding a Subsession
                          </h5>
                          <p className="text-neutral-600 pl-7">
                            Once created, your subsession appears as a card where you can add activities. You can set timing, add breaks between subsessions, and reorder them as needed.
                          </p>
                        </div>

                        {/* How to add activities */}
                        <div>
                          <h5 className="font-semibold text-neutral-800 mb-1 flex items-center gap-2">
                            <span className="w-5 h-5 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                            Adding Activities
                          </h5>
                          <p className="text-neutral-600 pl-7">
                            Click "Add Activity" within any subsession to choose from polls, quizzes, reflections, word clouds, and more. Activities will be delivered to participants in order during the live session.
                          </p>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-100">
                        <p className="text-xs text-neutral-500 text-center">
                          Tip: Use subsessions to create a structured learning flow for your participants.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {subsessions.length > 0 ? (
              <div className="space-y-4">
                {subsessions.map((subsession, index) => {
                  const subsessionId = (subsession.id || subsession._id)?.toString();
                  // Always use the main activities array as source of truth
                  // This prevents stale embedded activities from overriding local changes
                  const subsessionActivities = activities.filter((a) => a.subsessionId?.toString() === subsessionId);

                  return (
                    <React.Fragment key={subsessionId}>
                    <div
                      className="border-2 border-neutral-200 rounded-xl p-5 hover:border-primary-300 transition-colors bg-gradient-to-br from-white to-neutral-50"
                    >
                      {/* Subsession Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                            <span className="text-white font-bold text-base sm:text-lg">{index + 1}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-base sm:text-lg font-bold text-neutral-900 mb-1 break-words">{subsession.name || subsession.title}</h4>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-600 mb-3">
                              {(subsession.startTime && subsession.endTime) && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {subsession.duration || Math.round((new Date(subsession.endTime) - new Date(subsession.startTime)) / 60000)} minutes
                                </span>
                              )}
                              {subsession.timeFrom && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {(() => {
                                    const formatTime12 = (time24) => {
                                      if (!time24) return '';
                                      const [h, m] = time24.split(':');
                                      const hour = parseInt(h, 10);
                                      const ampm = hour >= 12 ? 'PM' : 'AM';
                                      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                      return `${hour12}:${m} ${ampm}`;
                                    };
                                    return `${formatTime12(subsession.timeFrom)}${subsession.timeTo ? ` - ${formatTime12(subsession.timeTo)}` : ''}`;
                                  })()}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                {subsessionActivities.length} {subsessionActivities.length === 1 ? 'activity' : 'activities'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          <button
                            onClick={() => handleOpenEditSubsession(subsession)}
                            className="px-3 py-1.5 text-primary-600 hover:bg-primary-50 rounded-lg font-medium text-xs sm:text-sm transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSubsession(subsessionId)}
                            className="px-3 py-1.5 text-error hover:bg-red-50 rounded-lg font-medium text-xs sm:text-sm transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Activities in this subsession */}
                      <div className="mt-4 border-t border-neutral-200 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="text-sm font-semibold text-neutral-700">Activities</h5>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              // Show activity type selector for this subsession
                              document.getElementById(`subsession-activities-${subsessionId}`).classList.toggle('hidden');
                            }}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add Activity
                          </Button>
                        </div>

                        {/* Activity Type Selector (hidden by default) */}
                        <div id={`subsession-activities-${subsessionId}`} className="hidden mb-4 p-3 sm:p-4 bg-white rounded-lg border-2 border-dashed border-neutral-300">
                          <p className="text-xs sm:text-sm font-medium text-neutral-700 mb-3">Choose activity type:</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                            {activityTypes.map((activityType) => (
                              <div
                                key={activityType.type}
                                onClick={() => {
                                  handleOpenActivityConfig(activityType, subsessionId);
                                  document.getElementById(`subsession-activities-${subsessionId}`).classList.add('hidden');
                                }}
                                className="cursor-pointer group"
                              >
                                <div className="border-2 border-neutral-200 rounded-lg p-3 hover:border-primary-500 hover:shadow-md transition-all duration-200">
                                  <div className={`w-10 h-10 ${activityType.bgColor} ${activityType.textColor} rounded-lg flex items-center justify-center text-xl mb-2 mx-auto group-hover:scale-110 transition-transform duration-200`}>
                                    {activityType.icon}
                                  </div>
                                  <h6 className={`text-xs font-semibold ${activityType.textColor} text-center`}>
                                    {activityType.name}
                                  </h6>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* List of activities with drag-and-drop reordering */}
                        {subsessionActivities.length > 0 ? (
                          <DraggableActivityList
                            activities={subsessionActivities}
                            subsessions={subsessions}
                            onReorder={(reordered) => handleReorderActivities(reordered, subsessionId)}
                            onEdit={handleEditActivity}
                            onDelete={handleDeleteActivity}
                            onDuplicate={handleDuplicateActivity}
                            onPreview={handlePreviewActivity}
                            onAssignToSubsession={(activity, targetSubsessionId) => handleAssignToSubsession(activity, targetSubsessionId)}
                          />
                        ) : (
                          <div className="text-center py-6 text-neutral-500 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200">
                            <svg className="w-10 h-10 mx-auto mb-2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-sm">No activities yet</p>
                            <p className="text-xs mt-1">Click "Add Activity" to add your first activity</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Break Indicator */}
                    {subsession.breakAfter > 0 && index < subsessions.length - 1 && (
                      <div className="flex items-center justify-center py-4">
                        <div className="flex items-center gap-3 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full">
                          <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-amber-700">
                            {subsession.breakAfter} minute break
                          </span>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <p>No subsessions yet. Add subsessions to organize your activities.</p>
              </div>
            )}

            {/* Add/Edit Subsession Modal */}
            {showAddSubsession && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 my-8">
                  <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-4">
                    {editingSubsession ? 'Edit Subsession' : 'Add Subsession'}
                  </h3>

                  <div className="space-y-4">
                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Title <span className="text-error">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={subsessionForm.title}
                        onChange={handleSubsessionFormChange}
                        placeholder="e.g., Introduction to Topic"
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        autoFocus
                      />
                    </div>

                    {/* Time From and To */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Time From <span className="text-neutral-500 text-xs">(Optional)</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={(() => {
                              if (!subsessionForm.timeFrom) return '';
                              const [h] = subsessionForm.timeFrom.split(':');
                              const hour = parseInt(h, 10);
                              if (hour === 0) return '12';
                              if (hour > 12) return String(hour - 12);
                              return String(hour);
                            })()}
                            onChange={(e) => {
                              const hour = parseInt(e.target.value, 10);
                              const [, minute] = (subsessionForm.timeFrom || '00:00').split(':');
                              const currentHour = parseInt((subsessionForm.timeFrom || '00:00').split(':')[0], 10);
                              const isPM = currentHour >= 12;
                              let newHour = hour;
                              if (isPM) {
                                newHour = hour === 12 ? 12 : hour + 12;
                              } else {
                                newHour = hour === 12 ? 0 : hour;
                              }
                              handleSubsessionFormChange({ target: { name: 'timeFrom', value: `${String(newHour).padStart(2, '0')}:${minute || '00'}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">--</option>
                            {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="text-neutral-500">:</span>
                          <select
                            value={subsessionForm.timeFrom ? subsessionForm.timeFrom.split(':')[1] : ''}
                            onChange={(e) => {
                              const [hour] = (subsessionForm.timeFrom || '00:00').split(':');
                              handleSubsessionFormChange({ target: { name: 'timeFrom', value: `${hour || '00'}:${e.target.value}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">--</option>
                            {['00', '15', '30', '45'].map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                          <select
                            value={(() => {
                              if (!subsessionForm.timeFrom) return 'AM';
                              const hour = parseInt(subsessionForm.timeFrom.split(':')[0], 10);
                              return hour >= 12 ? 'PM' : 'AM';
                            })()}
                            onChange={(e) => {
                              const [h, minute] = (subsessionForm.timeFrom || '00:00').split(':');
                              let hour = parseInt(h, 10);
                              const isPM = e.target.value === 'PM';
                              const wasAM = hour < 12;
                              if (isPM && wasAM) {
                                hour = hour === 0 ? 12 : hour + 12;
                              } else if (!isPM && !wasAM) {
                                hour = hour === 12 ? 0 : hour - 12;
                              }
                              handleSubsessionFormChange({ target: { name: 'timeFrom', value: `${String(hour).padStart(2, '0')}:${minute || '00'}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Time To <span className="text-neutral-500 text-xs">(Optional)</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            value={(() => {
                              if (!subsessionForm.timeTo) return '';
                              const [h] = subsessionForm.timeTo.split(':');
                              const hour = parseInt(h, 10);
                              if (hour === 0) return '12';
                              if (hour > 12) return String(hour - 12);
                              return String(hour);
                            })()}
                            onChange={(e) => {
                              const hour = parseInt(e.target.value, 10);
                              const [, minute] = (subsessionForm.timeTo || '00:00').split(':');
                              const currentHour = parseInt((subsessionForm.timeTo || '00:00').split(':')[0], 10);
                              const isPM = currentHour >= 12;
                              let newHour = hour;
                              if (isPM) {
                                newHour = hour === 12 ? 12 : hour + 12;
                              } else {
                                newHour = hour === 12 ? 0 : hour;
                              }
                              handleSubsessionFormChange({ target: { name: 'timeTo', value: `${String(newHour).padStart(2, '0')}:${minute || '00'}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">--</option>
                            {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="text-neutral-500">:</span>
                          <select
                            value={subsessionForm.timeTo ? subsessionForm.timeTo.split(':')[1] : ''}
                            onChange={(e) => {
                              const [hour] = (subsessionForm.timeTo || '00:00').split(':');
                              handleSubsessionFormChange({ target: { name: 'timeTo', value: `${hour || '00'}:${e.target.value}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">--</option>
                            {['00', '15', '30', '45'].map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                          <select
                            value={(() => {
                              if (!subsessionForm.timeTo) return 'AM';
                              const hour = parseInt(subsessionForm.timeTo.split(':')[0], 10);
                              return hour >= 12 ? 'PM' : 'AM';
                            })()}
                            onChange={(e) => {
                              const [h, minute] = (subsessionForm.timeTo || '00:00').split(':');
                              let hour = parseInt(h, 10);
                              const isPM = e.target.value === 'PM';
                              const wasAM = hour < 12;
                              if (isPM && wasAM) {
                                hour = hour === 0 ? 12 : hour + 12;
                              } else if (!isPM && !wasAM) {
                                hour = hour === 12 ? 0 : hour - 12;
                              }
                              handleSubsessionFormChange({ target: { name: 'timeTo', value: `${String(hour).padStart(2, '0')}:${minute || '00'}` } });
                            }}
                            className="w-20 px-2 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-1">
                      Set the scheduled start and end time for this subsession in your training agenda
                    </p>

                    {/* Break After */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Break After <span className="text-neutral-500 text-xs">(Optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <select
                          name="breakAfter"
                          value={isCustomBreak ? 'custom' : subsessionForm.breakAfter}
                          onChange={(e) => {
                            if (e.target.value === 'custom') {
                              // Switch to custom mode, keep current value or set to 1
                              setIsCustomBreak(true);
                              const currentValue = Number(subsessionForm.breakAfter);
                              if (currentValue === 0) {
                                handleSubsessionFormChange({ target: { name: 'breakAfter', value: 1 } });
                              }
                            } else {
                              // Switch to preset mode
                              setIsCustomBreak(false);
                              handleSubsessionFormChange(e);
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value={0}>No break</option>
                          <option value={5}>5 minutes</option>
                          <option value={10}>10 minutes</option>
                          <option value={15}>15 minutes</option>
                          <option value={20}>20 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={45}>45 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value="custom">Custom...</option>
                        </select>
                        {isCustomBreak && (
                          <input
                            type="number"
                            name="breakAfter"
                            value={subsessionForm.breakAfter}
                            onChange={handleSubsessionFormChange}
                            min={1}
                            max={120}
                            placeholder="Minutes"
                            className="w-24 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          />
                        )}
                        <span className="text-sm text-neutral-500">after this subsession</span>
                      </div>
                      <p className="text-xs text-neutral-500 mt-1">
                        Add a break time between this and the next subsession
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end mt-6">
                    <Button variant="ghost" onClick={() => {
                      setShowAddSubsession(false);
                      setEditingSubsession(null);
                    }}>
                      Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveSubsession}>
                      {editingSubsession ? 'Update' : 'Add'} Subsession
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Unassigned Activities Section */}
        {activities.filter(a => !a.subsessionId).length > 0 && (
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">Unassigned Activities</h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Activities not assigned to any subsession
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {activities.length > 0 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleExportActivities('csv')}
                        title="Export to CSV"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export CSV
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleExportActivities('json')}
                        title="Export to JSON"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Export JSON
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Draggable Activities List - only unassigned */}
              <DraggableActivityList
                activities={activities.filter(a => !a.subsessionId)}
                subsessions={subsessions}
                onReorder={handleReorderActivities}
                onEdit={handleEditActivity}
                onDelete={handleDeleteActivity}
                onDuplicate={handleDuplicateActivity}
                onPreview={handlePreviewActivity}
                onAssignToSubsession={handleAssignActivityToSubsession}
              />
            </div>
          </Card>
        )}

        {/* Session Mode Selector */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">Session Mode</h3>
            <p className="text-sm text-neutral-600 mb-6">Choose how activities are released to participants</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Non-Drip Mode */}
              <div
                onClick={() => setSessionMode('non-drip')}
                className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                  sessionMode === 'non-drip'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                {sessionMode === 'non-drip' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-1">Non-Drip Mode</h4>
                    <p className="text-sm text-neutral-600 mb-3">
                      All activities available immediately. Participants can complete them in any order.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Self-paced
                      </span>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                        Flexible
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Participants choose their pace</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Complete activities in any order</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Best for asynchronous sessions</span>
                  </div>
                </div>
              </div>

              {/* Drip Mode */}
              <div
                onClick={() => setSessionMode('drip')}
                className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${
                  sessionMode === 'drip'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                {sessionMode === 'drip' && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-neutral-900 mb-1 flex items-center gap-2">
                      Drip Mode
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Sequential
                      </span>
                    </h4>
                    <p className="text-sm text-neutral-600 mb-3">
                      Activities released one at a time in order. Next activity unlocks after completion.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Structured
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Guided
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Sequential activity release</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Prevent skipping ahead</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-neutral-700">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Best for live/structured sessions</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Mode Indicator */}
            <div className="mt-6 p-4 bg-neutral-50 rounded-lg flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                sessionMode === 'non-drip' ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                {sessionMode === 'non-drip' ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-600">Current Mode</p>
                <p className="text-lg font-semibold text-neutral-900">
                  {sessionMode === 'non-drip' ? 'Non-Drip Mode' : 'Drip Mode'}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Sticky Save Changes Button at Bottom */}
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-neutral-200 shadow-lg p-4 -mx-4 mt-6">
          <div className="flex items-center justify-between max-w-full">
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Remember to save your changes before leaving</span>
            </div>
            <button
              onClick={handleSaveAllChanges}
              disabled={isSaving}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center gap-2 text-base"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Activity Configuration Modal */}
      {showActivityConfig && selectedActivityType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 ${selectedActivityType.bgColor} rounded-xl flex items-center justify-center text-2xl`}>
                {selectedActivityType.icon}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">{editingActivity ? 'Edit' : 'Add'} {selectedActivityType.name}</h3>
                <p className="text-sm text-neutral-600">{selectedActivityType.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Activity Title <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={activityConfig.title}
                  onChange={(e) => handleActivityConfigChange('title', e.target.value)}
                  placeholder={`e.g., ${selectedActivityType.name} Activity`}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>

              {/* Multiple Questions (for Quiz) */}
              {selectedActivityType.type === 'quiz' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-neutral-700">
                      Questions <span className="text-error">*</span>
                    </label>
                    <span className="text-xs text-neutral-500">
                      {activityConfig.questions.length} question{activityConfig.questions.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  {activityConfig.questions.map((q, qIndex) => (
                    <div key={qIndex} className="border-2 border-neutral-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-neutral-900">Question {qIndex + 1}</h4>
                        {activityConfig.questions.length > 1 && (
                          <button
                            onClick={() => handleRemoveQuestion(qIndex)}
                            className="text-red-600 hover:text-red-700 text-xs font-medium"
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <textarea
                        value={q.question}
                        onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                        placeholder="What is the quiz question?"
                        rows={2}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />

                      <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-2">Options</label>
                        <div className="space-y-2">
                          {q.options.map((option, optIndex) => (
                            <div key={optIndex} className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleSetCorrectAnswer(qIndex, optIndex)}
                                className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${
                                  q.correctAnswer === optIndex
                                    ? 'bg-green-500 border-green-500'
                                    : 'border-neutral-300 hover:border-green-400'
                                }`}
                                title="Mark as correct answer"
                              >
                                {q.correctAnswer === optIndex && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleQuestionOptionChange(qIndex, optIndex, e.target.value)}
                                placeholder={`Option ${optIndex + 1}`}
                                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                              />
                              {q.options.length > 2 && (
                                <button
                                  onClick={() => handleRemoveQuestionOption(qIndex, optIndex)}
                                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => handleAddQuestionOption(qIndex)}
                          className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add Option
                        </button>
                        <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
                          <span className="w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                            <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                          Click to mark the correct answer
                        </p>
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={handleAddQuestion}
                    className="w-full py-2 px-4 border-2 border-dashed border-neutral-300 rounded-lg text-sm text-neutral-600 hover:border-primary-500 hover:text-primary-600 font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Question
                  </button>
                </div>
              )}

              {/* Feedback Activity - Show predefined questions info */}
              {selectedActivityType.type === 'feedback' && (
                <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-semibold text-primary-700">5 Predefined Feedback Questions</span>
                  </div>
                  <p className="text-xs text-primary-600 mb-3">
                    Feedback activities use standardized questions for consistent metrics. Participants will rate each on a 1-5 emoji scale.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-5 h-5 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">1</span>
                      <span className="text-neutral-700">How relevant and useful was this topic for your role?</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-5 h-5 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">2</span>
                      <span className="text-neutral-700">How effectively did the trainer help you understand and engage with this topic?</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-5 h-5 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">3</span>
                      <span className="text-neutral-700">How confident do you feel about applying this topic to overcome real work challenges?</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-5 h-5 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">4</span>
                      <span className="text-neutral-700">How likely are you to apply this learning and recommend it to others?</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs">
                      <span className="w-5 h-5 bg-primary-200 rounded-full flex items-center justify-center text-primary-700 font-bold flex-shrink-0">5</span>
                      <span className="text-neutral-700">To what extent did this topic address a real challenge you are currently facing?</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Question (for Poll and other types, not quiz or feedback) */}
              {selectedActivityType.type !== 'quiz' && selectedActivityType.type !== 'feedback' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Question <span className="text-error">*</span>
                  </label>
                  <textarea
                    value={activityConfig.question}
                    onChange={(e) => handleActivityConfigChange('question', e.target.value)}
                    placeholder={
                      selectedActivityType.type === 'poll'
                        ? 'What is your question for the poll?'
                        : selectedActivityType.type === 'wordcloud'
                        ? 'What topic should participants provide words for?'
                        : selectedActivityType.type === 'challenges'
                        ? 'e.g., What challenges are you facing in your current role?'
                        : selectedActivityType.type === 'actionplans'
                        ? 'e.g., What specific steps will you take to improve?'
                        : 'What question do you want to ask?'
                    }
                    rows={3}
                    className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              {/* Options (for Poll only, not Quiz) */}
              {selectedActivityType.type === 'poll' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Options <span className="text-error">*</span>
                  </label>
                  <div className="space-y-2">
                    {activityConfig.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleOptionChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        {activityConfig.options.length > 2 && (
                          <button
                            onClick={() => handleRemoveOption(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove option"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleAddOption}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Option
                  </button>
                </div>
              )}

              {/* Words (for Word Cloud) */}
              {selectedActivityType.type === 'wordcloud' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Predefined Words <span className="text-neutral-500 text-xs">(Optional - seed the word cloud)</span>
                  </label>
                  <div className="space-y-2">
                    {activityConfig.words.map((word, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={word}
                          onChange={(e) => handleWordChange(index, e.target.value)}
                          placeholder={`Word ${index + 1}`}
                          className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        {activityConfig.words.length > 1 && (
                          <button
                            onClick={() => handleRemoveWord(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove word"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleAddWord}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Word
                  </button>
                  <p className="text-xs text-neutral-500 mt-2">
                    Add words to seed the word cloud. Participants can also add their own words.
                  </p>
                </div>
              )}

              {/* Timer */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={activityConfig.timerEnabled}
                      onChange={(e) => handleActivityConfigChange('timerEnabled', e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-neutral-700">Enable timer for this activity</span>
                  </label>
                </div>

                {activityConfig.timerEnabled && (
                  <div className="pl-6">
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={activityConfig.duration}
                      onChange={(e) => handleActivityConfigChange('duration', parseInt(e.target.value) || 5)}
                      className="w-32 px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button variant="ghost" onClick={() => {
                setShowActivityConfig(false);
                setEditingActivity(null);
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveActivity}>
                {editingActivity ? 'Save Changes' : 'Add Activity'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Preview Modal */}
      <ActivityPreviewModal
        activity={previewActivity}
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewActivity(null);
        }}
        onEdit={handleEditActivity}
        onConfirmAdd={handleConfirmAddActivity}
      />

      {/* Delete Activity Confirmation */}
      <ConfirmDialog
        isOpen={deleteActivityConfirm !== null}
        onClose={() => setDeleteActivityConfirm(null)}
        onConfirm={confirmDeleteActivity}
        title="Delete Activity?"
        message={`Are you sure you want to delete "${deleteActivityConfirm?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Delete Subsession Confirmation */}
      <ConfirmDialog
        isOpen={deleteSubsessionConfirm !== null}
        onClose={() => setDeleteSubsessionConfirm(null)}
        onConfirm={confirmDeleteSubsession}
        title="Delete Subsession?"
        message="Are you sure you want to delete this subsession? All activities will be moved to the main session."
        confirmText="Delete"
        cancelText="Cancel"
        variant="warning"
      />

      {/* Delete Workbook Confirmation */}
      <ConfirmDialog
        isOpen={deleteWorkbookConfirm !== null}
        onClose={() => setDeleteWorkbookConfirm(null)}
        onConfirm={confirmDeleteWorkbook}
        title="Delete Workbook?"
        message="Are you sure you want to delete this workbook? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={cancelLeave}
        onConfirm={confirmLeave}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        confirmText="Yes, Leave"
        cancelText="No, Stay"
        variant="warning"
      />
    </DashboardLayout>
  );
};

export default SessionSetupPage;
